"use client";

import { useEffect, useState, useRef, useCallback } from "react";

export interface V11StreamLine {
  id: string;
  text: string;
  type: "thought" | "emotion" | "status" | "karma" | "output";
}

const POLL_INTERVAL_MS = 4000;

export function useKarmaV11Stream(enabled: boolean, userId = "mindaloo") {
  const [lines, setLines] = useState<V11StreamLine[]>([]);
  const [connected, setConnected] = useState(false);
  const esRef = useRef<EventSource | null>(null);

  const push = useCallback((type: V11StreamLine["type"], text: string) => {
    if (!text) return;
    setLines((prev) =>
      [...prev, { id: `${Date.now()}-${Math.random()}`, text, type }].slice(-120)
    );
  }, []);

  useEffect(() => {
    if (!enabled) {
      esRef.current?.close();
      esRef.current = null;
      setConnected(false);
      return;
    }

    push("status", "Live V11 stream connecting…");

    // Netlify's serverless functions can't sustain a long-lived SSE
    // connection (live=1) — it gets killed mid-stream, EventSource's
    // built-in auto-reconnect immediately retries, hits the same failure,
    // and repeats in a tight loop (ERR_HTTP2_PROTOCOL_ERROR, dozens of
    // times a minute). The server already has a "single pulse" mode — one
    // request, one response, closes itself — that was never actually
    // wired up client-side. Poll it periodically instead of holding one
    // connection open; each pulse completes and closes normally, so
    // there's nothing for Netlify to kill mid-stream.
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const runPulse = () => {
      if (cancelled) return;
      const url = `/api/karma-ac/stream?channel=thought-stream&userId=${encodeURIComponent(userId)}`;
      const es = new EventSource(url);
      esRef.current = es;

      es.addEventListener("connected", () => {
        setConnected(true);
      });

      es.addEventListener("karma", (e) => {
        try {
          const data = JSON.parse((e as MessageEvent).data);
          if (Array.isArray(data.reasoning)) {
            data.reasoning.forEach((r: string) =>
              push("thought", typeof r === "string" ? r : JSON.stringify(r))
            );
          }
          if (data.output) push("output", data.output);
          if (data.emotion?.label) push("emotion", `Feeling: ${data.emotion.label}`);
          if (data.message) push("karma", data.message);
        } catch {
          push("karma", (e as MessageEvent).data);
        }
      });

      es.addEventListener("thought", (e) => {
        try {
          const data = JSON.parse((e as MessageEvent).data);
          push("thought", data.text ?? data.content ?? JSON.stringify(data));
        } catch {
          push("thought", (e as MessageEvent).data);
        }
      });

      es.addEventListener("status", (e) => {
        try {
          const data = JSON.parse((e as MessageEvent).data);
          push("status", data.message ?? "");
        } catch {
          /* ignore */
        }
      });

      es.onerror = () => {
        // A single pulse closing after it's done also fires onerror
        // (readyState CLOSED) — that's expected, not a failure. Only
        // treat it as a real drop if we never got connected.
        es.close();
        if (esRef.current === es) esRef.current = null;
        if (!cancelled) timer = setTimeout(runPulse, POLL_INTERVAL_MS);
      };
    };

    runPulse();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      esRef.current?.close();
      esRef.current = null;
    };
  }, [enabled, userId, push]);

  return { lines, connected, clear: () => setLines([]) };
}
